import { useQuery } from '@tanstack/react-query';

import { Card, CardContent } from '@/components/ui/card';
import { api } from '@/services/api';
import { formatDateTime } from '@/lib/utils';
import type { RecordItem } from '@/types';

interface TimelineViewProps {
  prisonerId: string;
}

export function TimelineView({ prisonerId }: TimelineViewProps) {
  const { data: records, isLoading, error } = useQuery({
    queryKey: ['timeline', prisonerId],
    queryFn: () => api.getTimeline(prisonerId),
  });

  if (isLoading) {
    return <p className="text-muted-foreground">Lädt Timeline...</p>;
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-red-500">
          Fehler beim Laden: {(error as Error).message}
        </CardContent>
      </Card>
    );
  }

  if (!records?.length) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          Keine Einträge in der Timeline.
        </CardContent>
      </Card>
    );
  }

  // Group records by date
  const groupedRecords = records.reduce(
    (acc: Record<string, RecordItem[]>, record: RecordItem) => {
      const date = new Date(record.createdAt).toDateString();
      if (!acc[date]) acc[date] = [];
      acc[date].push(record);
      return acc;
    },
    {}
  );

  return (
    <div className="space-y-6">
      {Object.entries(groupedRecords).map(([date, items]) => (
        <div key={date}>
          <h3 className="mb-3 text-sm font-semibold text-muted-foreground">
            {new Date(date).toLocaleDateString('de-DE', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </h3>
          <div className="relative space-y-4 pl-6 before:absolute before:left-2 before:top-2 before:h-[calc(100%-16px)] before:w-0.5 before:bg-border">
            {(items as RecordItem[]).map((record) => (
              <div key={record.id} className="relative">
                <div
                  className={`absolute -left-4 top-2 h-3 w-3 rounded-full ${
                    record.needle === 1
                      ? 'bg-blue-500'
                      : record.needle === 2
                        ? 'bg-green-500'
                        : 'bg-orange-500'
                  }`}
                />
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium">{record.title}</h4>
                        <p className="text-sm text-muted-foreground">
                          {record.type} •{' '}
                          {formatDateTime(record.createdAt)}
                        </p>
                      </div>
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs ${
                          record.needle === 1
                            ? 'bg-blue-100 text-blue-800'
                            : record.needle === 2
                              ? 'bg-green-100 text-green-800'
                              : 'bg-orange-100 text-orange-800'
                        }`}
                      >
                        Nadel {record.needle}
                      </span>
                    </div>
                    {record.content && (
                      <p className="mt-2 text-sm">{record.content}</p>
                    )}
                    <div className="mt-2 flex flex-wrap gap-1">
                      {record.modules.map((m) => (
                        <span
                          key={m}
                          className="rounded bg-muted px-2 py-0.5 text-xs"
                        >
                          {m}
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
